/**
 * bridgeModel — pure pivot from the §5 resolve envelope to the additive EBITDA
 * component bridge (A3 module 1): Revenue − COGS − OPEX = EBITDA. No fetch (CD-7):
 * reuses the page's single /resolve, sliced to one site·period like A2's trendSeries.
 *
 * The bridge is the constitution's parent = Σ children invariant applied to the
 * leg-sum axis: `reconciles` is |Σ signed legs − served headline| < 0.005 (2 dp),
 * computed here from server truth, never re-derived cosmetically. A leg whose cell
 * carries a DQ flag (OPEX → KNOWN-WRONG, KAL-385) taints the bridge visibly; an
 * unserved component is an honest gap (null), never a zero — a gap can't reconcile.
 */

import type { Cell, ResolveResponse, Site } from '../../types/metrics'
import { scaleForDisplay } from './cellFormat'

/** The P&L composition identity (s-5pny), as data — not per-metric behaviour dispatch.
 *  sign is the waterfall direction; the headline is what the signed legs must sum to. */
const COMPONENTS: { id: string; sign: 1 | -1 }[] = [
  { id: 'revenue', sign: 1 },
  { id: 'cogs', sign: -1 },
  { id: 'opex', sign: -1 },
]
const HEADLINE_ID = 'ebitda'
const RECONCILE_TOLERANCE = 0.005 // §5: 2 dp

export interface BridgeLeg {
  id: string
  label: string
  sign: 1 | -1
  raw: number | null // raw £ (null = unserved gap)
  display: number | null // signed, display-scaled (£k) for the chart
  cell: Cell | null
  dqFlags: string[]
}

export interface BridgeModel {
  site: Site
  period: string | null
  legs: BridgeLeg[]
  headlineId: string
  headlineLabel: string
  headlineRaw: number | null
  headlineDisplay: number | null
  headlineCell: Cell | null
  legSumRaw: number | null // Σ signed legs (raw) — the derived headline, null if any gap
  reconciles: boolean // all served AND |legSum − headline| < tol
  hasGap: boolean // any leg or the headline unserved
  chartable: boolean // the running total and headline stay ≥ 0 — a waterfall can draw it
  dqFlags: string[] // union across served legs + headline
}

/** The kit Waterfall has no negative floor: a running total or headline that dips
 *  below zero (a loss month, or incoherent components) would draw off-canvas. When
 *  that happens the bridge falls back to an honest legs list, never a broken chart. */
function isChartable(legs: BridgeLeg[], headlineDisplay: number | null): boolean {
  if (headlineDisplay === null || headlineDisplay < 0) return false
  let cumulative = 0
  for (const leg of legs) {
    if (leg.display === null) return false
    cumulative += leg.display
    if (cumulative < 0) return false
  }
  return true
}

const keyOf = (metricId: string, site: Site, period: string) => `${metricId}|${site}|${period}`

function actualLookup(resolve: ResolveResponse, site: Site): Map<string, Cell> {
  const lookup = new Map<string, Cell>()
  for (const cell of resolve.cells) {
    if (cell.site === site && cell.plan_type === 'Actual') {
      lookup.set(keyOf(cell.metric_id, site, cell.period), cell)
    }
  }
  return lookup
}

/** Latest served period (for this site) among the components/headline — the bridge's
 *  default month. Honest: falls back to the resolve's last period if none serve. */
function latestServedPeriod(lookup: Map<string, Cell>, resolve: ResolveResponse, site: Site): string | null {
  const ids = [...COMPONENTS.map((c) => c.id), HEADLINE_ID]
  for (const period of [...resolve.periods].reverse()) {
    if (ids.some((id) => lookup.has(keyOf(id, site, period)))) return period
  }
  return resolve.periods[resolve.periods.length - 1] ?? null
}

function toLeg(cell: Cell | null, spec: { id: string; sign: 1 | -1 }): BridgeLeg {
  const raw = cell?.value ?? null
  return {
    id: spec.id,
    label: cell?.served_metric ?? spec.id,
    sign: spec.sign,
    raw,
    display: raw === null ? null : scaleForDisplay(raw * spec.sign, 'gbp_k'),
    cell,
    dqFlags: cell?.dq_flags ?? [],
  }
}

export function buildEbitdaBridge(resolve: ResolveResponse, site: Site, period?: string): BridgeModel {
  const lookup = actualLookup(resolve, site)
  const at = period ?? latestServedPeriod(lookup, resolve, site)
  const legs = COMPONENTS.map((spec) => toLeg(at ? lookup.get(keyOf(spec.id, site, at)) ?? null : null, spec))
  const headlineCell = at ? lookup.get(keyOf(HEADLINE_ID, site, at)) ?? null : null
  const headlineRaw = headlineCell?.value ?? null

  const allServed = legs.every((leg) => leg.raw !== null) && headlineRaw !== null
  const legSumRaw = allServed ? legs.reduce((sum, leg) => sum + (leg.raw as number) * leg.sign, 0) : null
  const reconciles =
    legSumRaw !== null && headlineRaw !== null && Math.abs(legSumRaw - headlineRaw) < RECONCILE_TOLERANCE
  const dqFlags = [
    ...new Set([...legs.flatMap((leg) => leg.dqFlags), ...(headlineCell?.dq_flags ?? [])]),
  ]

  return {
    site,
    period: at,
    legs,
    headlineId: HEADLINE_ID,
    headlineLabel: headlineCell?.served_metric ?? HEADLINE_ID,
    headlineRaw,
    headlineDisplay: headlineRaw === null ? null : scaleForDisplay(headlineRaw, 'gbp_k'),
    headlineCell,
    legSumRaw,
    reconciles,
    hasGap: !allServed,
    chartable: allServed && isChartable(legs, headlineRaw === null ? null : scaleForDisplay(headlineRaw, 'gbp_k')),
    dqFlags,
  }
}
